import { AddCircleOutline, SearchOutlined, Download as DownloadIcon } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import InputAdornment from '@mui/material/InputAdornment'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import React, { KeyboardEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PermissionAccess, PermissionKey, PermissionProvider } from '../../../../context'
import { Format, dateFormat } from '../../../../utility/date-format'
import { PATH } from '../../../../utility/path'
import { Preloader } from '../../../common'
import { ArchiveButton, CustomPagination, CustomTableCell, EditButton, ViewButton } from '../../../shared'
import { TADMembership } from '../../../../type'
import { API } from '../../../../api'
import { ConfirmationAlert } from '../../../shared/alerts'
import { usePermission } from '../../../../context/Permission'

export enum SharedGroupTitles {
  qlik = 'Qlik Group',
  pharmacy = 'Pharmacy Group',
  distributor = 'Distributor Group',
  ce = 'CE Group',
  omit = 'Omit Group',
  referral_type = 'Referral Type Group',
  reports = 'Report Group',
}

export enum SharedGroupTypes {
  qlik = 'qlik',
  pharmacy = 'pharmacy',
  distributor = 'distributor',
  ce = 'ce',
  omit = 'omit',
  referral_type = 'referral_type',
  reports = 'reports',
}

type ISharedGroupsListProps = {
  sharedType: SharedGroupTypes
  titleName: SharedGroupTitles
  sharedGroups: TADMembership[]
  setSearch: (search: string) => void
  setSharedGroup: (sharedGroup: string) => void
  totalCount: number
  page: number
  maxPageSize: number
  setPage: (page: number) => void
  setIsSharedGroupView: (isSharedView: boolean) => void
  isLoading: boolean
  search: string
  handleExportSharedGroupsButtonClick: () => void
  getSharedGroups?: () => Promise<void> | void
}

export const SharedGroupsList: React.FC<ISharedGroupsListProps> = ({
  sharedType,
  sharedGroups,
  setSearch,
  setSharedGroup,
  totalCount,
  page,
  maxPageSize,
  setPage,
  setIsSharedGroupView,
  titleName,
  isLoading,
  search,
  handleExportSharedGroupsButtonClick,
  getSharedGroups,
}) => {
  const navigation = useNavigate()
  const [inputValue, setInputValue] = useState(search)
  const [isLoadingArchive, setIsLoadingArchive] = useState(false)
  const [isConfirmModal, setIsConfirmModal] = useState(false)
  const [archiveSharedGroup, setArchiveSharedGroup] = useState('')
  const hasDetailsPermission = usePermission(PermissionKey.btns_users)

  useEffect(() => {
    setInputValue(search)
  }, [search])

  const renderPreloader = () => (
    <Grid
      container
      justifyContent='center'
    >
      <Preloader size='sm' />
    </Grid>
  )

  const renderToolbar = () => {
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setPage(1)
        setSearch(inputValue)
      }
    }
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
    }

    const handleCreateSharedType = () => {
      navigation(`${PATH.admin.ad_management.shared_groups}/new?type=${sharedType}`)
    }

    return (
      <Grid
        container
        spacing={2}
        sx={{ pb: 3 }}
      >
        <Grid
          item
          xs={4}
        >
          <TextField
            label='Search'
            size='small'
            fullWidth
            value={inputValue}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined />
                </InputAdornment>
              ),
            }}
            inputProps={{
              'data-testid': `${sharedType}-search`,
            }}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
          />
        </Grid>
        <Grid
          container
          item
          xs={2}
          justifyContent='flex-start'
        >
          <Button
            variant='outlined'
            startIcon={<DownloadIcon />}
            onClick={handleExportSharedGroupsButtonClick}
            data-testid={`${sharedType}-export`}
            className={'h-full'}
            disabled={sharedGroups.length === 0 || isLoading}
          >
            Export
          </Button>
        </Grid>
        <Grid
          container
          item
          xs={6}
          justifyContent='flex-end'
        >
          <PermissionProvider permission={PermissionKey.btns_shared_groups}>
            <Button
              variant='contained'
              startIcon={<AddCircleOutline />}
              onClick={handleCreateSharedType}
              data-testid={`${sharedType}-create`}
              className={'h-full'}
            >
              Create {titleName}
            </Button>
          </PermissionProvider>
        </Grid>
      </Grid>
    )
  }

  const HeaderCell = ({ children, className }: { children: React.ReactNode; className?: string }) => (
    <TableCell className={`no-wrap ${className}`}>
      <span
        className='t-w-200'
        style={{ fontWeight: 'bold' }}
      >
        {children}
      </span>
    </TableCell>
  )

  const getDeleteSuffix = (type: SharedGroupTypes) => {
    switch (type) {
      case SharedGroupTypes.ce:
        return 'ce/shared_groups'
      case SharedGroupTypes.qlik:
        return 'qlik/shared_groups'
      case SharedGroupTypes.pharmacy:
        return 'pharmacies/shared_groups'
      case SharedGroupTypes.distributor:
        return 'distributors/shared_groups'
      case SharedGroupTypes.omit:
        return 'omissions/shared_groups'
      case SharedGroupTypes.referral_type:
        return 'referral_types/shared_groups'
      case SharedGroupTypes.reports:
        return 'qlik_based_reports/shared_groups'
      default:
        return ''
    }
  }

  const handleArchiveSharedGroup = async () => {
    try {
      setIsLoadingArchive(true)
      await API.ADManagement.sharedGroups.delete(archiveSharedGroup || '', getDeleteSuffix(sharedType))
      setIsLoadingArchive(false)
      setIsConfirmModal(false)
      await getSharedGroups?.()
    } catch (err) {
      console.log('Error archiving shared group:', err)
    } finally {
      setArchiveSharedGroup('')
    }
  }

  const renderSharedGroups = () => {
    const renderHead = () => (
      <TableHead>
        <TableRow className='bg-base300'>
          <HeaderCell>Shared Group Name</HeaderCell>
          <HeaderCell>Description</HeaderCell>
          <HeaderCell>Updated At</HeaderCell>
          <HeaderCell> </HeaderCell>
        </TableRow>
      </TableHead>
    )

    const renderBody = () => {
      const handleEdit = (sharedGroup: string) => {
        navigation(`${PATH.admin.ad_management.shared_groups}/${sharedGroup}?type=${sharedType}`)
      }

      const handleView = (sharedGroup: string) => {
        setIsSharedGroupView(true)
        setSharedGroup(sharedGroup)
      }
      const handleArchive = (sharedGroup: TADMembership) => {
        setArchiveSharedGroup(sharedGroup.GroupName)
        setIsConfirmModal(true)
      }
      return (
        <TableBody>
          {sharedGroups.map((sharedGroup, index) => (
            <TableRow key={sharedGroup.AdGroupId}>
              <CustomTableCell
                dataTest={`sharedGroup-${index}-${sharedType}-name`}
                sx={{ minWidth: '200px' }}
              >
                {sharedGroup.GroupName}
              </CustomTableCell>
              <CustomTableCell
                dataTest={`sharedGroup-${index}-${sharedType}-description`}
                sx={{ minWidth: '200px' }}
              >
                {sharedGroup.Description}
              </CustomTableCell>

              <CustomTableCell
                dataTest={`sharedGroup-${index}-date`}
                sx={{ minWidth: '200px' }}
              >
                {dateFormat(sharedGroup.WhenChanged, Format['DD MMM YYYY'])} at {dateFormat(sharedGroup.WhenChanged, Format['HH MM'])}
              </CustomTableCell>
              <CustomTableCell
                dataTest={`sharedGroup-${index}-date`}
                sx={{ minWidth: '50px' }}
              >
                <Stack
                  direction={'row'}
                  spacing={1}
                  sx={{ width: '100%', height: '100%' }}
                  justifyContent={'end'}
                >
                  <ViewButton
                    onClick={() => handleView(sharedGroup.GroupName)}
                    dataTestId={`${sharedType}-${index}-view`}
                  />
                  <PermissionProvider
                    permission={PermissionKey.btns_shared_groups}
                    className='d-ib'
                  >
                    <EditButton
                      onClick={() => handleEdit(sharedGroup.GroupName)}
                      dataTestId={`${sharedType}-${index}-edit`}
                    />
                  </PermissionProvider>
                  <PermissionProvider
                    permission={PermissionKey.btns_shared_groups}
                    className='d-ib'
                  >
                    <ArchiveButton
                      onClick={() => handleArchive(sharedGroup)}
                      dataTestId={`${sharedType}-${index}-archive`}
                      disabled={hasDetailsPermission === PermissionAccess.Read}
                    />
                  </PermissionProvider>
                </Stack>
              </CustomTableCell>
            </TableRow>
          ))}
        </TableBody>
      )
    }

    const renderPagination = () => {
      const handlePagination = (event: React.ChangeEvent<unknown>, page_number: number) => {
        setPage(page_number)
      }

      return (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          mt={4}
        >
          <CustomPagination
            total_count={totalCount}
            max_page_size={maxPageSize}
            page_number={page}
            showFirstButton={false}
            showLastButton={false}
            handlePagination={handlePagination}
          />
        </Box>
      )
    }

    if (sharedGroups && sharedGroups.length > 0) {
      return (
        <div className='table-wrapper'>
          <div style={{ display: 'flex' }}>
            <TableContainer
              className='table-container main-table'
              style={{ flex: 4 }}
            >
              <Table>
                {renderHead()}
                {renderBody()}
              </Table>
            </TableContainer>
          </div>
          <div className='pagination-container'>{renderPagination()}</div>
        </div>
      )
    } else {
      return <div style={{ textAlign: 'center', fontWeight: 'bold', color: 'var(--pantoneBlue)' }}>No Results</div>
    }
  }

  return (
    <>
      {renderToolbar()}
      {isLoading ? renderPreloader() : renderSharedGroups()}
      <ConfirmationAlert
        isShow={isConfirmModal}
        title={
          <Typography
            fontWeight={'bold'}
            variant={'h6'}
            fontSize={'20px'}
            data-testid={'shared-group-action'}
          >
            Are you sure you want to delete this AD Group?
          </Typography>
        }
        content={
          <Typography data-testid={'shared-group-action'}>
            <strong>Please Note!</strong> Deleting this Active directory group will permanently remove all permissions and access rights
            granted through it
          </Typography>
        }
        dataTestId='confirmation'
        onClose={() => {
          setIsConfirmModal(false)
          setArchiveSharedGroup('')
        }}
        onCancel={() => {
          setIsConfirmModal(false)
          setArchiveSharedGroup('')
        }}
        onConfirm={handleArchiveSharedGroup}
        cancellationLabel='Cancel'
        confirmationLabel='Confirm'
        isLoading={isLoadingArchive}
        shouldAlignBetween
      />
    </>
  )
}
